import {
  type OperationKey,
  type OperationReference,
  useAsyncOperationQuery,
} from '@vrplatform/data';
import { useCallback, useEffect, useRef } from 'react';
import { useStore } from 'zustand';
import { useOperationTrackerStore } from './operationTracker.hooks';

const OPERATION_POLL_MIN_INTERVAL_MS = 500;
const OPERATION_POLL_MAX_INTERVAL_MS = 5_000;

/** The delay doubles every three reads, so short operations are noticed quickly and long ones settle at the cap. */
export const getOperationPollDelay = (settledReads: number) =>
  Math.min(
    OPERATION_POLL_MIN_INTERVAL_MS * 2 ** Math.floor(settledReads / 3),
    OPERATION_POLL_MAX_INTERVAL_MS
  );

export function OperationPoller({
  operationKey,
  reference,
}: {
  operationKey: OperationKey;
  reference: OperationReference;
}) {
  const store = useOperationTrackerStore();
  const status = useStore(
    store,
    (state) => state.operations[operationKey]?.status
  );
  const enabled = status === 'queued' || status === 'running';
  const query = useAsyncOperationQuery(reference, { enabled });

  const timeoutRef = useRef<number | undefined>(undefined);
  const requestRef = useRef<Promise<void> | null>(null);
  const didStartRef = useRef(false);
  const scheduledChecksRef = useRef(0);
  const enabledRef = useRef(enabled);
  const isFetchingRef = useRef(query.isFetching);

  enabledRef.current = enabled;
  isFetchingRef.current = query.isFetching;

  const clearPendingCheck = useCallback(() => {
    if (timeoutRef.current !== undefined) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const checkNow = useCallback(async () => {
    clearPendingCheck();
    if (!enabledRef.current) return;

    if (requestRef.current) return requestRef.current;

    // The initial enabled query is owned by TanStack. Do not overlap it with a
    // focus/online-triggered refetch; its settled render schedules one timer.
    if (isFetchingRef.current) return;

    const request = query
      .refetch({ cancelRefetch: false })
      .then(() => undefined)
      .finally(() => {
        requestRef.current = null;
      });

    requestRef.current = request;
    return request;
  }, [clearPendingCheck, query.refetch]);

  useEffect(() => {
    if (query.isNotFound) {
      store.getState().stopOperation(operationKey, 'unavailable');
      return;
    }

    if (query.error && !query.isTransientError) {
      store.getState().stopOperation(operationKey, 'error');
      return;
    }

    if (query.data) {
      store.getState().updateOperation(operationKey, query.data);
    }
  }, [
    operationKey,
    query.data,
    query.error,
    query.isNotFound,
    query.isTransientError,
    store,
  ]);

  useEffect(() => {
    clearPendingCheck();

    if (
      !enabled ||
      query.isFetching ||
      query.isNotFound ||
      (query.error && !query.isTransientError)
    ) {
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      scheduledChecksRef.current += 1;
      void checkNow();
    }, getOperationPollDelay(scheduledChecksRef.current));

    return clearPendingCheck;
  }, [
    checkNow,
    clearPendingCheck,
    enabled,
    query.dataUpdatedAt,
    query.error,
    query.errorUpdatedAt,
    query.isFetching,
    query.isNotFound,
    query.isTransientError,
  ]);

  useEffect(() => {
    if (didStartRef.current) return;

    didStartRef.current = true;
    if (enabled && !query.isFetching) void checkNow();
    // Registration must perform one immediate detail read. Subsequent reads
    // are controlled by the settled-query scheduling effect above.
  }, [checkNow, enabled, query.isFetching]);

  useEffect(() => {
    const handleImmediateCheck = () => {
      void checkNow();
    };

    window.addEventListener('focus', handleImmediateCheck);
    window.addEventListener('online', handleImmediateCheck);

    return () => {
      clearPendingCheck();
      window.removeEventListener('focus', handleImmediateCheck);
      window.removeEventListener('online', handleImmediateCheck);
    };
  }, [checkNow, clearPendingCheck]);

  return null;
}
